'use client'

import Link from 'next/link'
import { FileText, Lightbulb, Plus, TrendingUp, Clock, RefreshCw, Bot, Globe, FolderOpen, BarChart3 } from 'lucide-react'
import { useStats } from '@/hooks/useStats'
import { btn, card, text, layout } from '@/lib/theme'

export default function HomePage() {
  const { stats, loading: statsLoading, error: statsError, refetch } = useStats()
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ja-JP', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className={text.h1}>ダッシュボード</h1>
            <button
              onClick={refetch}
              disabled={statsLoading}
              className={btn.iconBlue}
              title="統計情報を更新"
            >
              <RefreshCw className={`h-5 w-5 ${statsLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400">
            Claude Code生成記事の包括的な管理プラットフォーム
          </p>
        </div>

        {/* 統計カード */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className={card.base}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">総記事数</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statsLoading ? (
                    <span className="animate-pulse bg-gray-200 dark:bg-gray-600 h-8 w-12 block rounded"></span>
                  ) : statsError ? (
                    <span className="text-red-500">--</span>
                  ) : (
                    stats?.total_articles || 0
                  )}
                </p>
              </div>
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
            </div>
          </div>

          <div className={card.base}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">今月の生成数</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statsLoading ? (
                    <span className="animate-pulse bg-gray-200 dark:bg-gray-600 h-8 w-12 block rounded"></span>
                  ) : statsError ? (
                    <span className="text-red-500">--</span>
                  ) : (
                    stats?.this_month_generated || 0
                  )}
                </p>
              </div>
              <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
            </div>
          </div>

          <div className={`${card.base} sm:col-span-2 lg:col-span-1`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">公開済み</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {statsLoading ? (
                    <span className="animate-pulse bg-gray-200 dark:bg-gray-600 h-8 w-12 block rounded"></span>
                  ) : statsError ? (
                    <span className="text-red-500">--</span>
                  ) : (
                    stats?.published_articles || 0
                  )}
                </p>
              </div>
              <BarChart3 className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
            </div>
          </div>
        </div>

        {/* メインナビゲーション */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link href="/articles" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <FileText className="h-12 w-12 text-blue-600 group-hover:text-blue-700" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-blue-700`}>記事管理</h3>
                  <p className={text.body}>記事の閲覧、編集、版管理を行います</p>
                </div>
              </div>
            </div>
          </Link>

          <Link href="/articles/new" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <Plus className="h-12 w-12 text-green-600 group-hover:text-green-700" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-green-700`}>記事作成</h3>
                  <p className={text.body}>新しい記事を手動で作成します</p>
                </div>
              </div>
            </div>
          </Link>

          <Link href="/agent-workflow" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <Bot className="h-12 w-12 text-orange-600 group-hover:text-orange-700" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-orange-700`}>自動ワークフロー</h3>
                  <p className={text.body}>AIによる記事生成・レビュー・推敲を自動実行します</p>
                </div>
              </div>
            </div>
          </Link>

          <Link href="/ideas" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <Lightbulb className="h-12 w-12 text-yellow-500 group-hover:text-yellow-600" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-yellow-600`}>記事アイデア</h3>
                  <p className={text.body}>記事のアイデアを管理・一覧します</p>
                </div>
              </div>
            </div>
          </Link>

          <Link href="/publications" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <Globe className="h-12 w-12 text-purple-600 group-hover:text-purple-700" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-purple-700`}>外部投稿管理</h3>
                  <p className={text.body}>外部プラットフォームへの投稿状況を管理します</p>
                </div>
              </div>
            </div>
          </Link>

          <Link href="/categories" className="group">
            <div className={card.hover + ' p-8'}>
              <div className="flex items-center space-x-4">
                <FolderOpen className="h-12 w-12 text-teal-600 group-hover:text-teal-700" />
                <div>
                  <h3 className={`${text.h3} group-hover:text-teal-700`}>カテゴリ・タグ管理</h3>
                  <p className={text.body}>記事のカテゴリとタグを管理します</p>
                </div>
              </div>
            </div>
          </Link>
        </div>

        {/* 最近の活動 */}
        <div className={`mt-8 sm:mt-12 ${card.base}`}>
          <div className={`${layout.flexBetween} mb-4`}>
            <h2 className={text.h3}>最近の記事</h2>
            <Link href="/articles" className={text.linkSm}>すべて見る</Link>
          </div>

          {statsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center space-x-3">
                    <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : statsError ? (
            <div className="text-center py-4">
              <p className={text.sub}>最近の記事を読み込めませんでした</p>
            </div>
          ) : stats?.recent_articles && stats.recent_articles.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_articles.map((article) => (
                <div key={article.id} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 min-w-0 flex-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                    <Link
                      href={`/articles/${article.id}`}
                      className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 text-sm truncate"
                    >
                      {article.title}
                    </Link>
                  </div>
                  <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                    <Clock className="h-3 w-3" />
                    <span>{formatDate(article.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className={text.sub}>まだ記事がありません</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}